import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  KeyIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "../../stores/auth";
import { supabase } from "../../lib/supabase";

interface License {
  id: string;
  license_key: string;
  status: string;
  label: string | null;
  trial_ends_at: string | null;
  purchased_at: string | null;
  support_ends_at: string | null;
  created_at: string;
  activations: { id: string }[];
}

type SupportStatus = "active" | "expiring_soon" | "expired" | "not_applicable";

function supportStatusFor(license: License): SupportStatus {
  if (!license.support_ends_at || license.status === "trial") {
    return "not_applicable";
  }
  const msLeft = new Date(license.support_ends_at).getTime() - Date.now();
  if (msLeft <= 0) return "expired";
  if (msLeft <= 30 * 86_400_000) return "expiring_soon";
  return "active";
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const statusStyles: Record<string, { bg: string; dot: string; text: string }> = {
  "in-use": {
    bg: "bg-forest-50",
    dot: "bg-forest-500",
    text: "text-forest-700",
  },
  available: {
    bg: "bg-sky-50",
    dot: "bg-sky-500",
    text: "text-sky-700",
  },
  active: {
    bg: "bg-forest-50",
    dot: "bg-forest-500",
    text: "text-forest-700",
  },
  trial: {
    bg: "bg-gold-50",
    dot: "bg-gold-500",
    text: "text-gold-700",
  },
  expired: {
    bg: "bg-red-50",
    dot: "bg-red-400",
    text: "text-red-700",
  },
  revoked: {
    bg: "bg-bark-100",
    dot: "bg-bark-400",
    text: "text-bark-600",
  },
};

function daysRemaining(dateStr: string): number {
  const end = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86_400_000));
}

function isInactiveStatus(status: string): boolean {
  return status === "revoked" || status === "expired";
}

export function Dashboard() {
  const schoolName = useAuthStore((s) => s.schoolName);
  const session = useAuthStore((s) => s.session);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [renewingLicenseId, setRenewingLicenseId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [acceptedPurchaseTerms, setAcceptedPurchaseTerms] = useState(false);

  useEffect(() => {
    async function fetchLicenses() {
      const { data } = await supabase
        .from("licenses")
        .select("*, activations(id)")
        .is("activations.deactivated_at", null)
        .order("created_at", { ascending: false });
      setLicenses(data ?? []);
      setLoading(false);
    }
    fetchLicenses();
  }, []);

  async function copyKey(key: string) {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.warn("Failed to copy to clipboard:", err);
    }
  }

  async function handleBuyLicence() {
    if (!session || !acceptedPurchaseTerms) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-checkout",
        {
          body: { account_id: session.user.id, kind: "initial", terms_version: "2026-09-20", terms_accepted: true },
        },
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : "Could not start checkout. Please try again.",
      );
      setCheckoutLoading(false);
    }
  }

  async function handleRenewSupport(licenseId: string) {
    if (!session || !acceptedPurchaseTerms) return;
    setRenewingLicenseId(licenseId);
    setCheckoutError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-checkout",
        {
          body: {
            account_id: session.user.id,
            kind: "renewal",
            license_id: licenseId,
            terms_version: "2026-09-20",
            terms_accepted: true,
          },
        },
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : "Could not start renewal. Please try again.",
      );
      setRenewingLicenseId(null);
    }
  }

  const hasActiveLicense = licenses.some((l) => l.status === "active");
  const trialLicense = licenses.find((l) => l.status === "trial");
  const hiddenCount = licenses.filter((l) => isInactiveStatus(l.status)).length;
  const visibleLicenses = showInactive
    ? licenses
    : licenses.filter((l) => !isInactiveStatus(l.status));

  return (
    <div className="workspace-page p-6 sm:p-8 lg:p-10">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-forest-950">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-bark-600">{schoolName}</p>
        </div>
        {!hasActiveLicense && (
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={handleBuyLicence}
              disabled={checkoutLoading || !acceptedPurchaseTerms}
              className="primary-action flex items-center gap-2 rounded-xl bg-gold-500 px-5 py-2.5 text-sm font-bold text-bark-900 shadow-lg shadow-gold-500/20 transition-all hover:bg-gold-400 hover:shadow-xl disabled:opacity-60"
            >
              <ShoppingCartIcon className="h-4 w-4" />
              {checkoutLoading ? "Redirecting..." : "Buy licence"}
            </button>
            {checkoutError && (
              <p className="text-sm text-red-600">{checkoutError}</p>
            )}
          </div>
        )}
      </div>

      <label className="paper-card mt-6 flex max-w-2xl items-start gap-3 rounded-xl border border-cream-300 bg-white p-4 text-sm text-bark-700">
        <input
          type="checkbox"
          checked={acceptedPurchaseTerms}
          onChange={(e) => setAcceptedPurchaseTerms(e.target.checked)}
          className="mt-1 accent-forest-700"
        />
        <span>
          Before purchasing or renewing, I agree to the{" "}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-forest-700 underline">
            Terms and conditions
          </Link>
          , including the one-off payment and 30-day refund terms. Support renewal is optional and is not automatic.
        </span>
      </label>

      {/* Trial countdown */}
      {trialLicense?.trial_ends_at && (
        <div className="mt-6 rounded-xl border border-gold-200 bg-gold-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold-100">
              <span className="font-display text-sm font-bold text-gold-700">
                {daysRemaining(trialLicense.trial_ends_at)}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gold-800">
                {daysRemaining(trialLicense.trial_ends_at)} days left on your
                trial
              </p>
              <p className="text-xs text-gold-700/70">
                Purchase a licence to keep full access after your trial ends.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Licenses */}
      <div className="mt-8">
        <div className="flex items-end justify-between gap-3">
          <h2 className="font-display text-lg font-semibold text-forest-950">
            Your licences
          </h2>
          {hiddenCount > 0 && (
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-bark-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-3.5 w-3.5 accent-forest-700"
              />
              Show revoked & expired ({hiddenCount})
            </label>
          )}
        </div>

        {loading ? (
          <div className="mt-5 flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest-300 border-t-forest-700" />
          </div>
        ) : visibleLicenses.length === 0 ? (
          <div className="paper-card mt-5 rounded-xl border border-cream-300/60 bg-white p-8 text-center shadow-sm">
            <KeyIcon className="mx-auto h-8 w-8 text-bark-600/30" />
            <p className="mt-3 text-sm text-bark-600">
              {licenses.length === 0
                ? "No licences yet. Start a trial or purchase a licence."
                : "No active licences. Toggle above to show revoked and expired licences."}
            </p>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {visibleLicenses.map((license) => {
              const displayStatus =
                license.status === "active"
                  ? license.activations.length > 0
                    ? "in-use"
                    : "available"
                  : license.status;
              const style = statusStyles[displayStatus] ?? statusStyles.expired;
              return (
                <div
                  key={license.id}
                  className="paper-card rounded-xl border border-cream-300/60 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${style.bg}`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${style.dot}`}
                          />
                          <span
                            className={`text-xs font-semibold capitalize ${style.text}`}
                          >
                            {displayStatus}
                          </span>
                        </span>
                        {license.label && (
                          <span className="text-sm text-bark-600">
                            {license.label}
                          </span>
                        )}
                      </div>

                      {/* Licence key */}
                      <div className="mt-4 flex items-center gap-2">
                        <code className="rounded-lg bg-cream-100 px-3 py-2 font-mono text-sm tracking-wider text-forest-900">
                          {license.license_key}
                        </code>
                        <button
                          onClick={() => copyKey(license.license_key)}
                          className="rounded-lg p-2 text-bark-600/50 transition-colors hover:bg-cream-100 hover:text-bark-700"
                          title="Copy licence key"
                        >
                          {copiedKey === license.license_key ? (
                            <CheckIcon className="h-4 w-4 text-forest-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Trial countdown */}
                      {license.status === "trial" &&
                        license.trial_ends_at && (
                          <p className="mt-2 text-xs text-bark-600/60">
                            Trial expires in{" "}
                            {daysRemaining(license.trial_ends_at)} days
                          </p>
                        )}

                      {/* Purchase date */}
                      {license.purchased_at && (
                        <p className="mt-2 text-xs text-bark-600/60">
                          Purchased {formatDate(license.purchased_at)}
                        </p>
                      )}

                      {/* Support status */}
                      {(() => {
                        const support = supportStatusFor(license);
                        if (support === "not_applicable") return null;

                        const ends = license.support_ends_at
                          ? formatDate(license.support_ends_at)
                          : null;

                        const pill =
                          support === "active"
                            ? {
                                wrap: "bg-forest-50 text-forest-700",
                                dot: "bg-forest-500",
                              }
                            : support === "expiring_soon"
                              ? {
                                  wrap: "bg-gold-50 text-gold-800",
                                  dot: "bg-gold-500",
                                }
                              : {
                                  wrap: "bg-red-50 text-red-700",
                                  dot: "bg-red-400",
                                };

                        const label =
                          support === "active"
                            ? `Updates and support until ${ends}`
                            : support === "expiring_soon"
                              ? `Support ends ${ends}`
                              : `Support ended ${ends}`;

                        const showRenew =
                          support === "expiring_soon" || support === "expired";

                        return (
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${pill.wrap}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} />
                              {label}
                            </span>
                            {showRenew && (
                              <button
                                onClick={() => handleRenewSupport(license.id)}
                                disabled={renewingLicenseId === license.id || !acceptedPurchaseTerms}
                                className="rounded-lg border border-forest-700 px-3 py-1.5 text-xs font-semibold text-forest-800 transition-all hover:bg-forest-50 disabled:opacity-60"
                              >
                                {renewingLicenseId === license.id
                                  ? "Redirecting..."
                                  : "Renew support"}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
